/**
 * White-label branding. Two sources, resolved once per session and seeded from
 * localStorage for a flash-free first paint:
 *
 *   1. Partner subdomain  <slug>.<domain>  → that partner's branding
 *      (GET /api/platform/partner-brand/:slug). Powers per-partner login.
 *   2. Otherwise → the deployment's global branding from /api/settings/public
 *      (Settings → Customization: logo, favicon, homepage, name).
 *
 * Components read the logo via `useBranding()`; siteConfig reads the name via
 * `getBrand()`. See platform partners.routes / customization.routes.
 */
import { useEffect, useState } from 'react';
import { config } from '../../features/auth/config';
import { colorMix } from '../../shared/lib/color-mix';

interface Brand {
  logoUrl: string | null;
  faviconUrl: string | null;
  homepageHtml: string | null;
  name: string | null;
  description: string | null;
  /** Brand primary/secondary colors (platform brand or partner), else null. */
  primaryColor: string | null;
  secondaryColor: string | null;
  /** Multiplier for the brand logo's rendered height (1 = default). */
  logoScale: number;
}

/** Clamp a raw logo-scale value to a sane range; default 1. */
function parseScale(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0.5 && n <= 1.5 ? n : 1;
}

/**
 * The partner slug when the host is a `<slug>.<baseDomain>` subdomain, else null.
 *
 * `VITE_BASE_DOMAIN` (per deployment: `testmug.com`, `fresherbot.com`,
 * `localhost`) makes this exact — the label(s) before the base domain are the
 * subdomain. Falls back to a labels-count heuristic if the env isn't set.
 * Works for local emulation via `<slug>.localhost`.
 */
function subdomainSlug(): string | null {
  try {
    const h = window.location.hostname.toLowerCase();
    if (!h || /^[\d.]+$/.test(h)) return null;
    const ignore = (s: string) => !s || s === 'www' || s === 'app' || s === 'api';
    // Runtime value first, build-time second. VITE_BASE_DOMAIN is inlined by
    // Vite when the bundle is built, so a deployment that was built without it
    // cannot be corrected by editing .env — the empty string is already in the
    // JavaScript. The server publishes window.__BASE_DOMAIN__ from its own
    // BASE_DOMAIN on every index.html it serves, so .env plus a restart is
    // enough. See injectBaseDomain() in api/server.ts.
    const runtime = ((window as unknown as { __BASE_DOMAIN__?: string }).__BASE_DOMAIN__ || '').toLowerCase().trim();
    const built = ((import.meta as unknown as { env: Record<string, string> }).env.VITE_BASE_DOMAIN || '').toLowerCase().trim();
    const base = runtime || built;

    if (base) {
      if (h === base || h === `www.${base}`) return null;   // apex
      if (!h.endsWith(`.${base}`)) return null;             // unrelated host
      const first = h.slice(0, -(base.length + 1)).split('.')[0];
      return ignore(first) ? null : first;
    }

    // Fallback (no VITE_BASE_DOMAIN): treat <slug>.localhost or <slug>.<d>.<tld>.
    const parts = h.split('.');
    if (parts[parts.length - 1] === 'localhost') return parts.length >= 2 && !ignore(parts[0]) ? parts[0] : null;
    if (h === 'localhost' || parts.length < 3) return null;
    return ignore(parts[0]) ? null : parts[0];
  } catch { return null; }
}

const SLUG = subdomainSlug();
const LS_KEY = 'fb_brand_v1:' + (SLUG ?? 'default');
const EMPTY: Brand = { logoUrl: null, faviconUrl: null, homepageHtml: null, name: null, description: null, primaryColor: null, secondaryColor: null, logoScale: 1 };
// Absolute URLs (partner logo hosted elsewhere) pass through; storage paths get
// the API base prepended.
const abs = (u?: string | null) => (!u ? null : /^https?:\/\//i.test(u) ? u : `${config.api.baseUrl}${u}`);
const fkHeaders = { [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue };

function readLS(): { brand: Brand; partner: boolean } | null {
  try { const s = localStorage.getItem(LS_KEY); if (!s) return null; const o = JSON.parse(s); return { brand: { ...EMPTY, ...o }, partner: !!o._p }; } catch { return null; }
}

const seed = readLS();
let cache: Brand | null = seed?.brand ?? null;
// Whether this host resolved to an actual partner (not just any subdomain) —
// gates the root→login redirect so non-partner subdomains are unaffected.
let partnerResolved = seed?.partner ?? false;
let loaded = cache !== null;
let inflight: Promise<Brand> | null = null;
const listeners = new Set<() => void>();

function commit(b: Brand, isPartner = false): Brand {
  cache = b; loaded = true; partnerResolved = isPartner;
  try { localStorage.setItem(LS_KEY, JSON.stringify({ ...b, _p: isPartner })); } catch { /* ignore */ }
  listeners.forEach((l) => l());
  return b;
}

/** The deployment's global (platform) branding — testmug's logo/name/homepage. */
function fetchGlobal(): Promise<Brand> {
  return fetch(`${config.api.baseUrl}/api/settings/public`, { headers: fkHeaders })
    .then((r) => (r.ok ? r.json() : {}))
    .then((j: { brandLogoUrl?: string; brandFaviconUrl?: string; brandHomepageHtml?: string; brandName?: string; brandDescription?: string; brandPrimaryColor?: string; brandSecondaryColor?: string; brandLogoScale?: string }): Brand => ({
      logoUrl: abs(j.brandLogoUrl), faviconUrl: abs(j.brandFaviconUrl), homepageHtml: (j.brandHomepageHtml ?? '').trim() || null,
      name: (j.brandName ?? '').trim() || null, description: (j.brandDescription ?? '').trim() || null,
      primaryColor: (j.brandPrimaryColor ?? '').trim() || null, secondaryColor: (j.brandSecondaryColor ?? '').trim() || null,
      logoScale: parseScale(j.brandLogoScale),
    }))
    .catch((): Brand => cache ?? EMPTY);
}

function load(): Promise<Brand> {
  if (!inflight) {
    if (SLUG) {
      // Partner subdomain: partner's own brand, but the LOGO falls back to the
      // platform (testmug) logo when the partner hasn't uploaded one — never
      // the bundled FresherBot default. If the slug isn't a partner, use global.
      inflight = Promise.all([
        fetch(`${config.api.baseUrl}/api/platform/partner-brand/${encodeURIComponent(SLUG)}`, { headers: fkHeaders })
          .then((r) => (r.ok ? r.json() : null)).catch(() => null),
        fetchGlobal(),
      ]).then(([pj, global]: [{ partner?: { name: string; logoUrl: string | null; primaryColor: string | null } } | null, Brand]) => {
        const p = pj?.partner;
        if (!p) return commit(global, false);
        const partnerLogo = abs(p.logoUrl);
        return commit({
          // Partner's OWN logo only — when absent, the UI shows the partner name
          // as a wordmark (not the platform logo, not FresherBot).
          logoUrl: partnerLogo,
          faviconUrl: global.faviconUrl, // favicon stays the platform (testmug) one, not the partner logo
          homepageHtml: null,                            // subdomains route to login, not a homepage
          name: p.name,
          description: null,
          primaryColor: p.primaryColor ?? null,
          secondaryColor: null,
          logoScale: 1,
        }, true);
      });
    } else {
      inflight = fetchGlobal().then(commit);
    }
  }
  return inflight;
}

/** Non-hook accessor (localStorage-seeded) for siteConfig. */
export function getBrand(): Brand {
  return cache ?? EMPTY;
}

/** Apply brand text immediately after the admin saves — updates cache + seed. */
export function setBrandOverride(patch: Partial<Pick<Brand, 'name' | 'description'>>): void {
  commit({ ...(cache ?? EMPTY), ...patch });
}

export function useBranding(): Brand & { loaded: boolean; isSubdomain: boolean; isPartnerSubdomain: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    void load();
    return () => { listeners.delete(l); };
  }, []);
  // `isSubdomain` is known synchronously from the hostname (no fetch) — use it
  // to route/blank instantly and avoid flashing the platform homepage.
  return { ...(cache ?? EMPTY), loaded, isSubdomain: !!SLUG, isPartnerSubdomain: partnerResolved };
}

/** Apply the custom favicon to <head> when branding loads. Call once at the app root. */
export function useApplyBranding(): void {
  const { faviconUrl, primaryColor } = useBranding();
  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = faviconUrl;
  }, [faviconUrl]);
  // Expose the brand accent as CSS variables so the whole app UI (buttons,
  // links, active states — anything reading C.accent / APP_C.accent) themes to
  // the deployment's color. Unset → the var()'s built-in fallback applies.
  useEffect(() => {
    const root = document.documentElement;
    if (primaryColor) {
      const soft = /^#[0-9a-fA-F]{6}$/.test(primaryColor) ? `${colorMix(primaryColor, 12)}` : primaryColor;
      root.style.setProperty('--brand-accent', primaryColor);
      root.style.setProperty('--brand-accent-soft', soft);
    } else {
      root.style.removeProperty('--brand-accent');
      root.style.removeProperty('--brand-accent-soft');
    }
  }, [primaryColor]);
}
